import { useEffect, useMemo } from "react";
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import {
  convexAt,
  cutoutRing,
  edgeFrame,
  insetPolygon,
  insidePoly,
  lineIntersect,
  scaleAbout,
  shapeFrom,
} from "@/lib/planGeometry";
import { figuredWoodTexture, woodTexture } from "@/lib/threeTextures";

export const CAB_H = 34.5;
export const TOE_H = 4;
export const UPPER_H = 30;
export const UPPER_D = 12;
export const UPPER_LIFT = 18;
const BODY_H = CAB_H - TOE_H;
const OVERHANG = 1.5;
const TOE_IN = 3;
const FRONT_T = 0.75;
const REVEAL = 0.15;
const RAIL = 2.25;

export const CAB_COLORS = {
  White: "#f1ede4",
  Grey: "#8b8d90",
  Olive: "#69725d",
  Navy: "#243447",
  Black: "#1d1d20",
  "Natural Maple": "#c4b5a0",
  "Honey Maple": "#d2b287",
  Oak: "#c8a470",
  Walnut: "#5b3e2c",
  Espresso: "#38231a",
};
export const CAB_WOOD = {
  "Natural Maple": "maple",
  "Honey Maple": "honeymaple",
  Oak: "oak",
  Walnut: "walnut",
  Espresso: "espresso",
};
// quotes saved before the palette change may still say "Sage"
export const finishOf = (name) =>
  CAB_COLORS[name] ? name : name === "Sage" ? "Olive" : "White";
export const finishSlug = (name) => name.toLowerCase().replace(/\s+/g, "-");

// front = doors face out here, wall = sits against a wall, drop = a waterfall panel
// covers the side, seam = joins the next piece
export const edgeRole = (e = {}) => {
  const p = (e.profile || "").toLowerCase();
  if (e.appliance) return "appliance";
  if (e.seam) return "seam";
  if (Number(e.drop_in) > 0 || p.includes("waterfall")) return "drop";
  if (Number(e.backsplash_height_in) > 0 || isUnfinishedEdge(e)) return "wall";
  return "front";
};

// The Draw tab decides "unfinished" by the edge style's *shape*; the 3D view is handed the
// shop's edge styles so it applies the same rule. The name heuristic only covers drawings
// rendered without a style list (customer portal).
let EDGE_STYLE_SHAPES = null;
export const configureEdgeStyles = (styles) => {
  EDGE_STYLE_SHAPES =
    Array.isArray(styles) && styles.length
      ? Object.fromEntries(
          styles.map((s) => [
            String(s.name || "").trim().toLowerCase(),
            s.shape || "",
          ]),
        )
      : null;
};
export const isUnfinishedEdge = (e = {}) => {
  const p = (e.profile || "").trim().toLowerCase();
  if (EDGE_STYLE_SHAPES && p in EDGE_STYLE_SHAPES) {
    return EDGE_STYLE_SHAPES[p] === "unfinished";
  }
  return !p || p.includes("unfinished") || p.includes("wall") || p === "none";
};

// Upper cabinets hang only over sides the drawing marks Unfinished (against a wall) —
// never over a finished edge, even one carrying a backsplash — and only on straight,
// axis-aligned walls so they face the same way as the base run beneath them.
const upperEdge = (pts, i, e) => {
  if (!isUnfinishedEdge(e) || e.appliance || e.seam || Number(e.drop_in) > 0) return false;
  const A = pts[i];
  const B = pts[(i + 1) % pts.length];
  const ang = Math.abs((Math.atan2(B[1] - A[1], B[0] - A[0]) * 180) / Math.PI) % 90;
  return Math.min(ang, 90 - ang) <= 10;
};

// Box with UVs in inches so wood grain reads at real scale (groups: +x -x +y -y +z -z)
const box = (w, h, d, horizGrain = false) => {
  const g = new THREE.BoxGeometry(w, h, d);
  const uv = g.attributes.uv;
  const dims = [
    [d, h],
    [d, h],
    [w, d],
    [w, d],
    [w, h],
    [w, h],
  ];
  for (let f = 0; f < 6; f++) {
    const [du, dv] = dims[f];
    for (let k = 0; k < 4; k++) {
      const i = f * 4 + k;
      const u = uv.getX(i) * du;
      const v = uv.getY(i) * dv;
      if (horizGrain) uv.setXY(i, v, u);
      else uv.setXY(i, u, v);
    }
  }
  return g;
};

// Door and drawer parts: softly rounded edges catch the light like real milled stock.
// UVs come from each vertex's dominant face so the grain still runs in inches.
const rbox = (w, h, d, horizGrain = false) => {
  const r = Math.min(0.1, Math.min(w, h, d) * 0.3);
  const g = new RoundedBoxGeometry(w, h, d, 2, r);
  const pos = g.attributes.position;
  const nor = g.attributes.normal;
  const uv = g.attributes.uv;
  for (let i = 0; i < pos.count; i++) {
    const nx = Math.abs(nor.getX(i));
    const ny = Math.abs(nor.getY(i));
    const nz = Math.abs(nor.getZ(i));
    let u;
    let v;
    if (nx >= ny && nx >= nz) {
      u = pos.getZ(i);
      v = pos.getY(i);
    } else if (ny >= nz) {
      u = pos.getX(i);
      v = pos.getZ(i);
    } else {
      u = pos.getX(i);
      v = pos.getY(i);
    }
    if (horizGrain) uv.setXY(i, v, u);
    else uv.setXY(i, u, v);
  }
  return g;
};

// local frame: x along the edge from O, y up, z outward (right-handed)
const frameMatrix = (O, e, n) =>
  new THREE.Matrix4().set(
    e[0],
    0,
    n[0],
    O[0],
    0,
    1,
    0,
    0,
    e[1],
    0,
    n[1],
    O[1],
    0,
    0,
    0,
    1,
  );

const panel = (
  x0,
  x1,
  yTop,
  yBot,
  type,
  style,
  out,
  hw,
  side = "right",
  handle = "top",
) => {
  const w = x1 - x0;
  const h = yTop - yBot;
  if (w <= 0.5 || h <= 0.5) return;
  const cx = (x0 + x1) / 2;
  const cy = (yTop + yBot) / 2;
  const horiz = type === "drawer";
  const rail = Math.min(RAIL, h / 3.2, w / 3.2);
  // dark backer just behind each front so the reveals read as shadow lines
  out.shadow.push(box(w + 0.24, h + 0.24, 0.06).translate(cx, cy, 0.03));
  if (style.door === "shaker" && rail >= 1.2) {
    const inner = FRONT_T - 0.3;
    out.fronts.push(
      rbox(rail, h, FRONT_T).translate(x0 + rail / 2, cy, FRONT_T / 2),
      rbox(rail, h, FRONT_T).translate(x1 - rail / 2, cy, FRONT_T / 2),
      rbox(w - 2 * rail, rail, FRONT_T, true).translate(
        cx,
        yTop - rail / 2,
        FRONT_T / 2,
      ),
      rbox(w - 2 * rail, rail, FRONT_T, true).translate(
        cx,
        yBot + rail / 2,
        FRONT_T / 2,
      ),
    );
    out.panels.push(
      box(w - 2 * rail + 0.2, h - 2 * rail + 0.2, inner, horiz).translate(
        cx,
        cy,
        inner / 2,
      ),
    );
    // shadow groove where the centre panel tucks under the frame
    const gw = w - 2 * rail;
    const gh = h - 2 * rail;
    const gd = FRONT_T - inner;
    const gz = inner + gd / 2;
    out.shadow.push(
      box(0.1, gh, gd).translate(x0 + rail + 0.05, cy, gz),
      box(0.1, gh, gd).translate(x1 - rail - 0.05, cy, gz),
      box(gw, 0.1, gd).translate(cx, yTop - rail - 0.05, gz),
      box(gw, 0.1, gd).translate(cx, yBot + rail + 0.05, gz),
    );
  } else {
    out.panels.push(rbox(w, h, FRONT_T, horiz).translate(cx, cy, FRONT_T / 2));
  }
  if (!hw || style.hardware === "none") return;
  const isDoor = type === "door";
  const low = handle === "bottom";
  const hx = isDoor ? (side === "right" ? x1 - 1.75 : x0 + 1.75) : cx;
  if (style.hardware === "knobs") {
    const hy = isDoor ? (low ? yBot + 2.5 : yTop - 2.5) : cy;
    out.metal.push(
      new THREE.SphereGeometry(0.6, 16, 12).translate(hx, hy, FRONT_T + 0.95),
      new THREE.CylinderGeometry(0.22, 0.3, 0.6, 12)
        .rotateX(Math.PI / 2)
        .translate(hx, hy, FRONT_T + 0.3),
    );
    return;
  }
  const pl = Math.min(isDoor ? 5 : 6, Math.max(2.5, (isDoor ? h : w) - 3));
  const hy = isDoor ? (low ? yBot + 2 + pl / 2 : yTop - 2 - pl / 2) : cy;
  const bar = isDoor ? box(0.45, pl, 0.45) : box(pl, 0.45, 0.45);
  out.metal.push(bar.translate(hx, hy, FRONT_T + 1.05));
  const off = pl / 2 - 0.5;
  [-off, off].forEach((o) =>
    out.metal.push(
      box(0.3, 0.3, 1.0).translate(
        isDoor ? hx : hx + o,
        isDoor ? hy + o : hy,
        FRONT_T + 0.5,
      ),
    ),
  );
};

// one base cabinet: top drawer + door(s), a sink base (false front), or a 3-drawer stack
const unit = (x0, x1, kind, style, out) => {
  const w = x1 - x0;
  const top = -REVEAL;
  const bottom = -(BODY_H - REVEAL);
  if (kind === "drawers") {
    const ys = [top, -6, -6 - REVEAL, -16, -16 - REVEAL, bottom];
    for (let j = 0; j < 3; j++)
      panel(x0, x1, ys[2 * j], ys[2 * j + 1], "drawer", style, out, true);
    return;
  }
  panel(x0, x1, top, -6, "drawer", style, out, kind !== "sink");
  const dy0 = -6 - REVEAL;
  if (w > 21) {
    const mid = (x0 + x1) / 2;
    panel(x0, mid - REVEAL / 2, dy0, bottom, "door", style, out, true, "right");
    panel(mid + REVEAL / 2, x1, dy0, bottom, "door", style, out, true, "left");
  } else panel(x0, x1, dy0, bottom, "door", style, out, true, "right");
};

// Standard cabinet-run split (~24" units). Shared by base and wall runs so upper
// doors sit at the same widths as the base cabinets below them.
const runUnits = (s0, s1) => {
  const R = s1 - s0;
  if (R < 7) return [];
  let n = Math.max(1, Math.round(R / 24));
  while (R / n > 36) n++;
  const uw = R / n;
  return Array.from({ length: n }, (_, k) => [
    s0 + k * uw,
    s0 + (k + 1) * uw,
    k,
    n,
  ]);
};

const runFronts = (s0, s1, cuts, style, out) => {
  runUnits(s0, s1).forEach(([us, ue, k, n]) => {
    const hit = cuts.find((c) => c.s >= us && c.s <= ue);
    let kind =
      (n >= 3 && k % 3 === 2) || (n === 2 && k === 1) ? "drawers" : "doors";
    if (hit?.kind.includes("sink")) kind = "sink";
    else if (hit?.kind.includes("cooktop")) kind = "drawers";
    unit(us + REVEAL / 2, ue - REVEAL / 2, kind, style, out);
  });
};

const merged = (parts) => {
  if (!parts.length) return null;
  const g = mergeGeometries(parts);
  parts.forEach((p) => p.dispose());
  return g;
};

const trimFor = (adjRole, convex) =>
  adjRole === "front"
    ? convex
      ? -FRONT_T
      : FRONT_T
    : adjRole === "wall"
      ? 0.25
      : 0;

// How far the counter extends inward behind edge i — the cabinet "depth" at that face.
const inwardDepth = (pts, i) => {
  const n = pts.length;
  const A = pts[i];
  const B = pts[(i + 1) % n];
  const f = edgeFrame(pts, i); // f.nx/f.ny point outward
  const mid = [(A[0] + B[0]) / 2, (A[1] + B[1]) / 2];
  const ix = -f.nx;
  const iy = -f.ny;
  const from = [mid[0] + ix * 0.05, mid[1] + iy * 0.05];
  const far = [mid[0] + ix * 1e5, mid[1] + iy * 1e5];
  let best = Infinity;
  for (let k = 0; k < n; k++) {
    if (k === i) continue;
    const C = pts[k];
    const D = pts[(k + 1) % n];
    const hit = lineIntersect(from, far, C, D);
    if (!hit) continue;
    const dot = (D[0] - C[0]) * (hit[0] - C[0]) + (D[1] - C[1]) * (hit[1] - C[1]);
    const segLen2 = (D[0] - C[0]) ** 2 + (D[1] - C[1]) ** 2;
    if (dot < -1e-6 || dot > segLen2 + 1e-6) continue; // hit must lie on the segment
    const fwd = (hit[0] - mid[0]) * ix + (hit[1] - mid[1]) * iy;
    if (fwd > 0.1 && fwd < best) best = fwd; // forward along the inward ray
  }
  return best;
};

// Edges that should carry door/drawer fronts: finished ("front") edges that are the
// shallow face of the run (depth behind ≤ the edge's own length), not the deep end panels.
const doorFrontEdges = (pts, roles) => {
  const fronts = roles.map((r, i) => (r === "front" ? i : -1)).filter((i) => i >= 0);
  const out = new Set();
  fronts.forEach((i) => {
    const b = pts[(i + 1) % pts.length];
    const len = Math.hypot(b[0] - pts[i][0], b[1] - pts[i][1]);
    const depth = inwardDepth(pts, i);
    if (!Number.isFinite(depth) || depth <= len + 1e-6) out.add(i);
  });
  return out.size ? out : new Set(fronts);
};

// Wall cabinets along every wall edge: 12" deep, 30" tall, hung 18" above the counter.
// Doors are full height with the handles at the bottom, and runs are trimmed where two
// walls meet so the corner box is not doubled up.
const upperRun = (s0, s1, style, out, yBot, yTop) => {
  runUnits(s0, s1).forEach(([u0, u1, k, n]) => {
    const us = u0 + REVEAL / 2;
    const ue = u1 - REVEAL / 2;
    if (ue - us > 21) {
      const mid = (us + ue) / 2;
      panel(us, mid - REVEAL / 2, yTop, yBot, "door", style, out, true, "right", "bottom");
      panel(mid + REVEAL / 2, ue, yTop, yBot, "door", style, out, true, "left", "bottom");
    } else {
      const side = k < n / 2 ? "right" : "left";
      panel(us, ue, yTop, yBot, "door", style, out, true, side, "bottom");
    }
  });
};

export const buildUppers = (piece, style, thick) => {
  const pts = piece.points;
  const n = pts.length;
  const edges = piece.edges || [];
  const roles = pts.map((_, i) => (upperEdge(pts, i, edges[i] || {}) ? "wall" : "front"));
  const y0 = thick + UPPER_LIFT;
  const bodies = [];
  const fronts = [];
  const panels = [];
  const metal = [];
  const shadow = [];
  for (let i = 0; i < n; i++) {
    if (roles[i] !== "wall") continue;
    const A = pts[i];
    const B = pts[(i + 1) % n];
    const L = Math.hypot(B[0] - A[0], B[1] - A[1]);
    if (L < 12) continue;
    const f = edgeFrame(pts, i);
    const nIn = [-f.nx, -f.ny];
    let e = [(B[0] - A[0]) / L, (B[1] - A[1]) / L];
    let O = A;
    const tA = roles[(i - 1 + n) % n] === "wall" ? UPPER_D : 0;
    const tB = roles[(i + 1) % n] === "wall" ? UPPER_D : 0;
    let s0 = tA;
    let s1 = L - tB;
    if (-e[1] * nIn[0] + e[0] * nIn[1] < 0) {
      e = [-e[0], -e[1]];
      O = B;
      s0 = tB;
      s1 = L - tA;
    }
    const M = frameMatrix(O, e, nIn);
    bodies.push(
      box(L, UPPER_H, UPPER_D)
        .translate(L / 2, y0 + UPPER_H / 2, UPPER_D / 2)
        .applyMatrix4(M),
    );
    const out = { fronts: [], panels: [], metal: [], shadow: [] };
    upperRun(s0, s1, style, out, y0 + REVEAL, y0 + UPPER_H - REVEAL);
    const lift = (g) => g.translate(0, 0, UPPER_D).applyMatrix4(M);
    out.fronts.forEach((g) => fronts.push(lift(g)));
    out.panels.forEach((g) => panels.push(lift(g)));
    out.metal.forEach((g) => metal.push(lift(g)));
    out.shadow.forEach((g) => shadow.push(lift(g)));
  }
  return {
    body: merged(bodies),
    fronts: merged(fronts),
    panels: merged(panels),
    metal: merged(metal),
    shadow: merged(shadow),
  };
};

// Carcass inset under the overhang, toe kick, and door/drawer fronts along every
// finished edge. Built in piece space (X = x, Y = up, Z = y) under the stone at y = 0.
export const buildCabinet = (piece, style) => {
  const pts = piece.points;
  const n = pts.length;
  const roles = pts.map((_, i) => edgeRole((piece.edges || [])[i]));
  const doorEdges = doorFrontEdges(pts, roles);
  const inner =
    insetPolygon(
      pts,
      roles.map((r) => (r === "front" ? OVERHANG : 0)),
    ) || scaleAbout(pts, 0.965);
  const toeIn =
    insetPolygon(
      inner,
      roles.map((r) => (r === "front" ? TOE_IN : 0)),
    ) || scaleAbout(inner, 0.9);
  // the top 12" of the box is open under sinks / cooktops so the basin shows through
  const holes = (piece.cutouts || [])
    .filter((c) => !(c.kind || "").toLowerCase().includes("radius"))
    .map(cutoutRing)
    .filter((ring) => ring.every((p) => insidePoly(p, inner)));
  const upper = new THREE.ExtrudeGeometry(shapeFrom(inner, holes), {
    depth: 12,
    bevelEnabled: false,
  });
  upper.rotateX(-Math.PI / 2);
  upper.translate(0, -12, 0);
  const lower = new THREE.ExtrudeGeometry(shapeFrom(inner), {
    depth: BODY_H - 12,
    bevelEnabled: false,
  });
  lower.rotateX(-Math.PI / 2);
  lower.translate(0, -BODY_H, 0);
  const body = merged([upper, lower]);
  const toe = new THREE.ExtrudeGeometry(shapeFrom(toeIn), {
    depth: TOE_H,
    bevelEnabled: false,
  });
  toe.rotateX(-Math.PI / 2);
  toe.translate(0, -CAB_H, 0);

  const cutCenters = (piece.cutouts || []).map((c) => ({
    x: c.x + (Number(c.w) || 6) / 2,
    y: c.y + (Number(c.h) || 6) / 2,
    kind: (c.kind || "").toLowerCase(),
  }));
  const fronts = [];
  const panels = [];
  const metal = [];
  const shadow = [];
  for (let i = 0; i < n; i++) {
    if (!doorEdges.has(i)) continue;
    const A = inner[i];
    const B = inner[(i + 1) % n];
    const L = Math.hypot(B[0] - A[0], B[1] - A[1]);
    if (L < 7) continue;
    const f = edgeFrame(pts, i);
    let e = [(B[0] - A[0]) / L, (B[1] - A[1]) / L];
    let O = A;
    const tA = trimFor(roles[(i - 1 + n) % n], convexAt(pts, i));
    const tB = trimFor(roles[(i + 1) % n], convexAt(pts, (i + 1) % n));
    let s0 = tA;
    let s1 = L - tB;
    if (-e[1] * f.nx + e[0] * f.ny < 0) {
      e = [-e[0], -e[1]];
      O = B;
      s0 = tB;
      s1 = L - tA;
    }
    const cuts = cutCenters
      .map((c) => {
        const dx = c.x - O[0];
        const dy = c.y - O[1];
        return {
          s: dx * e[0] + dy * e[1],
          d: dx * f.nx + dy * f.ny,
          kind: c.kind,
        };
      })
      .filter((c) => Math.abs(c.d) < 30);
    const out = { fronts: [], panels: [], metal: [], shadow: [] };
    runFronts(s0, s1, cuts, style, out);
    const M = frameMatrix(O, e, [f.nx, f.ny]);
    out.fronts.forEach((g) => fronts.push(g.applyMatrix4(M)));
    out.panels.forEach((g) => panels.push(g.applyMatrix4(M)));
    out.metal.forEach((g) => metal.push(g.applyMatrix4(M)));
    out.shadow.forEach((g) => shadow.push(g.applyMatrix4(M)));
  }
  return {
    body,
    toe,
    fronts: merged(fronts),
    panels: merged(panels),
    metal: merged(metal),
    shadow: merged(shadow),
  };
};

export const useCabinetMaterials = (finish) => {
  const mats = useMemo(() => {
    const wood = CAB_WOOD[finish];
    const hex = CAB_COLORS[finish] || CAB_COLORS.White;
    // lacquered wood: straight grain on the carcass, stiles and rails; figured veneer panels
    const lacquer = {
      roughness: 0.46,
      metalness: 0,
      clearcoat: 0.18,
      clearcoatRoughness: 0.45,
      envMapIntensity: 0.4,
    };
    const body = wood
      ? new THREE.MeshPhysicalMaterial({ map: woodTexture(wood), ...lacquer })
      : new THREE.MeshPhysicalMaterial({
          // satin paint
          color: hex,
          roughness: 0.36,
          metalness: 0,
          clearcoat: 0.3,
          clearcoatRoughness: 0.4,
          envMapIntensity: 0.7,
        });
    const panel = wood
      ? new THREE.MeshPhysicalMaterial({
          map: figuredWoodTexture(wood),
          ...lacquer,
        })
      : body;
    const toe = new THREE.MeshStandardMaterial({
      color: new THREE.Color(hex).multiplyScalar(0.45),
      roughness: 0.85,
    });
    const metal = new THREE.MeshStandardMaterial({
      color: "#d3d6db",
      metalness: 0.92,
      roughness: 0.26,
      envMapIntensity: 1,
    });
    // shadow lines are a deep shade of the finish, not black, so they read as depth
    const shadow = new THREE.MeshStandardMaterial({
      color: new THREE.Color(hex).multiplyScalar(0.3),
      roughness: 1,
    });
    return { body, panel, toe, metal, shadow };
  }, [finish]);
  useEffect(
    () => () => new Set(Object.values(mats)).forEach((m) => m.dispose()),
    [mats],
  );
  return mats;
};

export const Cabinet = ({ piece, mats, style, onPointerDown }) => {
  const built = useMemo(
    () => buildCabinet(piece, style),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [piece.points, piece.edges, piece.cutouts, style.door, style.hardware],
  );
  useEffect(
    () => () => {
      built.body.dispose();
      built.toe.dispose();
      built.fronts?.dispose();
      built.panels?.dispose();
      built.metal?.dispose();
      built.shadow?.dispose();
    },
    [built],
  );
  const lift = Number(piece.lift) || 0;
  // a raised top (bar height, step-up) gets a taller plinth so the run still meets the floor
  const s = (lift + TOE_H) / TOE_H;
  return (
    <group
      onPointerDown={
        onPointerDown
          ? (e) => {
              e.stopPropagation();
              onPointerDown(piece, e);
            }
          : undefined
      }
    >
      {lift > 0 && (
        <PiecePlaced piece={{ ...piece, lift: 0 }}>
          <mesh
            geometry={built.toe}
            material={mats.toe}
            scale={[1, s, 1]}
            position={[0, CAB_H * (s - 1), 0]}
            receiveShadow
          />
        </PiecePlaced>
      )}
      <PiecePlaced piece={piece}>
        <mesh
          geometry={built.body}
          material={mats.body}
          castShadow
          receiveShadow
        />
        <mesh geometry={built.toe} material={mats.toe} receiveShadow />
        <BuiltParts built={built} mats={mats} />
      </PiecePlaced>
    </group>
  );
};

export const UpperCabinet = ({ piece, mats, style, thick }) => {
  const built = useMemo(
    () => buildUppers(piece, style, thick),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [piece.points, piece.edges, style.door, style.hardware, thick],
  );
  useEffect(
    () => () => {
      built.body?.dispose();
      built.fronts?.dispose();
      built.panels?.dispose();
      built.metal?.dispose();
      built.shadow?.dispose();
    },
    [built],
  );
  if (!built.body) return null;
  return (
    <PiecePlaced piece={piece}>
      <mesh
        geometry={built.body}
        material={mats.body}
        castShadow
        receiveShadow
      />
      <BuiltParts built={built} mats={mats} />
    </PiecePlaced>
  );
};

// Piece space (X = x, Y = up, Z = y) placed and rotated like the stone above it
export const PiecePlaced = ({ piece, children }) => {
  const cx = piece.length_in / 2;
  const cy = piece.width_in / 2;
  const rot = (-(Number(piece.rot) || 0) * Math.PI) / 180;
  return (
    <group
      position={[piece.x + cx, Number(piece.lift) || 0, piece.y + cy]}
      rotation={[0, rot, 0]}
    >
      <group position={[-cx, 0, -cy]}>{children}</group>
    </group>
  );
};

const BuiltParts = ({ built, mats }) => (
  <>
    {built.shadow && <mesh geometry={built.shadow} material={mats.shadow} />}
    {built.fronts && (
      <mesh
        geometry={built.fronts}
        material={mats.body}
        castShadow
        receiveShadow
      />
    )}
    {built.panels && (
      <mesh
        geometry={built.panels}
        material={mats.panel}
        castShadow
        receiveShadow
      />
    )}
    {built.metal && (
      <mesh geometry={built.metal} material={mats.metal} castShadow />
    )}
  </>
);
